import { useSEO } from "@/hooks/useSEO";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { FileText, Scale, Ban, BadgeDollarSign, ShieldAlert } from "lucide-react";

const RENTAL_TERMS = [
  "Renter must be 25 years or older and hold a valid driver's license for at least 2 years. A photo of the driver's license is required at booking.",
  "Cancellations made more than 24 hours before pickup are refunded less a 20% cancellation charge. Cancellations within 24 hours of the scheduled pickup time are NON-REFUNDABLE.",
  "Vehicle is delivered with a full tank of fuel and must be returned at the same level.",
  "Basic insurance is included. Renter is responsible for the first $500 of any damage claim.",
  "Vehicles must be driven on the left-hand side of the road in accordance with Bahamian traffic laws.",
  "Vehicle must remain on New Providence and Paradise Island. Off-road use is prohibited.",
  "Additional drivers must be 25 or older and registered with Easy Drive Car Rental before operating the vehicle.",
  "Only registered drivers may operate the vehicle. If an unauthorized driver is found or caught driving the vehicle, this contract is cancelled immediately without refund and the vehicle will be recovered.",
  "Late returns incur a fee of $25 per hour. Extensions must be added to the existing booking before the scheduled return time — otherwise late fees are charged to the final invoice.",
];

const PAYMENT_TERMS = [
  "A refundable security deposit of $150 is held at pickup and released once the vehicle is returned in the same condition and return photos are submitted.",
  "A 3% processing fee applies to all card transactions.",
  "Optional add-ons (Paradise Island bridge pass — $8.99 service fee + $3.00 per trip, baby seat, additional driver) are charged at the rates shown at checkout.",
  "PayPal and Zelle reservations are confirmed once payment is verified. Include your booking reference in the payment note.",
];

const LEGAL_TERMS = [
  "This agreement is governed by the laws of the Commonwealth of The Bahamas.",
  "Easy Drive Car Rental may recover the vehicle at the renter's expense if any term of this agreement is breached.",
  "The renter is responsible for all traffic violations, tolls and fines incurred during the rental period.",
  "Personal data and uploaded documents are used solely to process your rental and are never sold to third parties.",
];

const Section = ({ icon: Icon, title, items, testid, numbered }) => (
  <section data-testid={testid} className="bg-card border border-border rounded-2xl p-8">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-full border border-primary/30 flex items-center justify-center shrink-0">
        <Icon size={16} className="gold-text" />
      </div>
      <h2 className="font-display text-2xl sm:text-3xl tracking-tight">{title}</h2>
    </div>
    {numbered ? (
      <ol className="mt-5 space-y-2.5 text-sm text-muted-foreground leading-relaxed list-decimal list-inside">
        {items.map((item, i) => <li key={i}>{item}</li>)}
      </ol>
    ) : (
      <ul className="mt-5 space-y-2.5 text-sm text-muted-foreground leading-relaxed list-disc list-inside">
        {items.map((item, i) => <li key={i}>{item}</li>)}
      </ul>
    )}
  </section>
);

export default function Terms() {
  useSEO({ title: "Terms & Conditions", description: "Easy Drive Car Rental terms & conditions: rental agreement terms, deposit and payment terms, driver requirements and legal details.", path: "/terms" });
  return (
    <div data-testid="terms-page" className="max-w-4xl mx-auto px-6 lg:px-10 pt-32 pb-24">
      <p className="text-[11px] font-ui tracking-[0.3em] uppercase text-primary">Easy Drive Car Rental</p>
      <h1 className="mt-3 font-display text-5xl sm:text-6xl tracking-tight">
        Terms & <span className="italic gold-text">Conditions</span>
      </h1>
      <p className="mt-5 text-muted-foreground max-w-2xl leading-relaxed">
        These are the exact terms that appear in your digital rental contract. By
        completing a booking and signing the contract, you agree to everything below.
      </p>

      <div className="mt-14 space-y-8">
        <Section icon={FileText} title="Rental Agreement Terms" items={RENTAL_TERMS} testid="terms-rental-section" numbered />
        <Section icon={BadgeDollarSign} title="Deposits & Payments" items={PAYMENT_TERMS} testid="terms-payment-section" />
        <Section icon={ShieldAlert} title="Unauthorized Drivers" testid="terms-unauthorized-section"
          items={[
            "Only drivers named and registered on the rental agreement may operate the vehicle.",
            "If an UNAUTHORIZED driver is found or caught driving the vehicle, the rental contract will be CANCELLED IMMEDIATELY without refund and the vehicle will be recovered.",
            "All liability for incidents involving an unauthorized driver rests entirely with the renter.",
          ]}
        />
        <Section icon={Scale} title="Legal" items={LEGAL_TERMS} testid="terms-legal-section" />
      </div>

      <div className="mt-14 bg-card border border-primary/30 rounded-2xl p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <p className="text-sm text-muted-foreground max-w-lg leading-relaxed flex items-start gap-2">
          <Ban size={15} className="gold-text mt-0.5 shrink-0" />
          Questions about these terms? Message us on WhatsApp at +1 (242) 813-1775 or
          email bookings@easydrivecarrental.com.
        </p>
        <Link to="/policies">
          <Button data-testid="terms-policies-button" variant="outline" className="rounded-full border-border bg-transparent hover:bg-black/5 hover:text-primary font-ui shrink-0">
            View Rental Policies
          </Button>
        </Link>
      </div>
    </div>
  );
}
